import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Checkbox, Flex, Input, Link,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Stack, Text, useToast,
} from '@chakra-ui/react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { getAndroidPackageName, getAndroidPackages, getAndroidRegistrationState } from './androidVerificationPackages';

const HELP_URL = 'https://support.google.com/googleplay/android-developer/answer/16761053';
const CONSOLE_URL = 'https://play.google.com/console/';

export default function AndroidVerification({ apps, canEdit }) {
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [certificateFingerprint, setCertificateFingerprint] = useState('');
  const [guidePackage, setGuidePackage] = useState(null);
  const [search, setSearch] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState('');
  const toast = useToast();
  const packages = useMemo(() => getAndroidPackages(apps), [apps]);
  const missing = apps.filter((app) => !getAndroidPackageName(app));
  const stateFor = (packageName) => getAndroidRegistrationState(records[packageName], certificateFingerprint);
  const pending = packages.filter(({ packageName }) => stateFor(packageName) !== 'REGISTERED');

  const refreshChecklist = useCallback(async () => {
    const { data } = await httpsCallable(functions, 'getAndroidRegistrationChecklist')();
    setRecords(data.packages || {});
    setCertificateFingerprint(data.certificateFingerprint || '');
    setLoaded(true);
    setLoadError('');
  }, []);

  // Load the indicator on mount and refresh shared state whenever the dialog opens.
  useEffect(() => {
    refreshChecklist().catch((error) => {
      setLoadError(`Unable to load the registration checklist: ${error.message}`);
      setLoaded(false);
    });
  }, [open, refreshChecklist]);

  const checkWithGoogle = async () => {
    setChecking(true);
    setCheckError('');
    try {
      const { data } = await httpsCallable(functions, 'checkAndroidRegistration')({ forceRefresh: true });
      await refreshChecklist();
      if (data.errors.length) {
        setCheckError(data.errors.map((error) => `${error.packageName}: ${error.message}`).join(' '));
      }
      toast({ title: `Google status: ${data.checked} checked, ${data.cached} recently checked, ${data.total} total`, status: data.errors.length ? 'warning' : 'success', isClosable: true });
    } catch (error) {
      setCheckError(error.message);
    } finally {
      setChecking(false);
    }
  };

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied`, status: 'success', duration: 2000 });
    } catch {
      toast({ title: `Could not copy. Select and copy the ${label.toLowerCase()} shown.`, status: 'error' });
    }
  };

  const guideApp = packages.find((entry) => entry.packageName === guidePackage);
  const guideName = guideApp ? `${guideApp.apps[0].name}${guideApp.apps[0].managed ? ' (Managed)' : ''}` : '';
  const query = search.trim().toLowerCase();
  const visible = packages.filter(({ packageName, apps: entries }) =>
    (!pendingOnly || stateFor(packageName) !== 'REGISTERED') &&
    [packageName, ...entries.flatMap((app) => [app.name, app.bundleId])].join(' ').toLowerCase().includes(query)
  );

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} aria-haspopup="dialog" gap={2}>
        Android verification
        <Badge colorScheme={loadError ? 'red' : !loaded ? 'gray' : pending.length || missing.length ? 'orange' : 'green'}>
          {loadError ? 'Unavailable' : !loaded ? 'Loading…' : `${pending.length} pending${missing.length ? ` · ${missing.length} missing ID` : ''}`}
        </Badge>
      </Button>
      <Modal isOpen={open} onClose={() => setOpen(false)} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader pr={12}>Android developer verification</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={5}>
              <Text color="gray.600" fontSize="sm">
                Google registration status · {loaded ? `${packages.length - pending.length} of ${packages.length} packages registered with the configured signing certificate` : 'Status unavailable until the checklist loads'}.
                {' '}Status is updated only from Google’s API. Use Guide to register for any package that needs registration.
              </Text>
              {loadError && <Alert status="error"><AlertIcon />{loadError}</Alert>}
              <Box borderWidth="1px" borderRadius="lg" p={4}>
                <Flex gap={3} align="center" justify="space-between" wrap="wrap">
                  <Text fontSize="sm" fontWeight="semibold">Google registration status</Text>
                  <Button size="sm" variant="outline" isLoading={checking} isDisabled={!canEdit || !loaded || packages.length === 0} onClick={checkWithGoogle}>Check with Google</Button>
                </Flex>
                <Text fontSize="xs" color="gray.600" mt={2}>Checks each package name together with the configured SHA-256 signing certificate. After completing registration in Play Console, press Check with Google to fetch the latest status.</Text>
                {checkError && <Alert status="error" mt={3} fontSize="sm"><AlertIcon />{checkError}</Alert>}
              </Box>
              {!canEdit && <Text fontSize="sm" color="gray.600">Publishing permission is required to refresh Google status.</Text>}
              {missing.length > 0 && (
                <Alert status="warning" alignItems="flex-start"><AlertIcon />
                  <Box fontSize="sm">Missing package ID: {missing.map((app) => app.appName || app.id).join(', ')}. Add packageName or packageId to these App records so they can be tracked.</Box>
                </Alert>
              )}
              <Flex gap={3} wrap="wrap" align="center">
                <Input aria-label="Search packages" placeholder="Search app or package ID" value={search} onChange={(event) => setSearch(event.target.value)} flex="1" minW="180px" />
                <Checkbox isChecked={pendingOnly} onChange={(event) => setPendingOnly(event.target.checked)}>Pending only</Checkbox>
              </Flex>
              <Stack spacing={3}>
                {visible.map(({ packageName, apps: entries }) => {
                  const record = records[packageName];
                  const state = stateFor(packageName);
                  const registered = state === 'REGISTERED';
                  return (
                    <Box key={packageName} borderWidth="1px" borderRadius="lg" p={4}>
                      <Flex gap={3} align="flex-start" justify="space-between" wrap="wrap">
                        <Box flex="1" minW="0">
                          <Text fontWeight="semibold" fontSize="sm">{entries.map((app) => `${app.name}${app.managed ? ' (Managed)' : ''}`).join(' · ')}</Text>
                          <Text as="code" display="block" fontSize="sm" overflowWrap="anywhere" my={1}>{packageName}</Text>
                          {entries.filter((app) => app.bundleId && app.bundleId !== packageName).map((app) => <Text key={app.id} fontSize="xs" overflowWrap="anywhere">Bundle ID: {app.bundleId}</Text>)}
                          <Text fontSize="xs" color={registered ? 'green.700' : 'gray.600'}>
                            {registered ? 'Registered with this signing certificate' : state === 'NOT_REGISTERED' ? 'Not registered with this signing certificate' : state === 'REGISTERED_WITH_ANOTHER_CERTIFICATE_FINGERPRINT' ? 'Registered with a different signing certificate' : 'Not checked with this signing certificate yet'}
                          </Text>
                          {state !== 'UNKNOWN' && record?.google?.checkedAt && <Text fontSize="xs" color="gray.600" mt={1}>
                            Last checked: {new Date(record.google.checkedAt).toLocaleString()}
                          </Text>}
                        </Box>
                      </Flex>
                      <Flex mt={3} gap={3} justify="space-between" align="center" wrap="wrap">
                        <Checkbox isChecked={registered} isDisabled aria-label={`Registered: ${packageName}`}>Registered by Google</Checkbox>
                        {!registered && <Button size="sm" colorScheme="orange" aria-haspopup="dialog" onClick={() => setGuidePackage(packageName)}>Guide to register</Button>}
                      </Flex>

                    </Box>
                  );
                })}
                {visible.length === 0 && <Text color="gray.600" fontSize="sm">{packages.length ? 'No packages match these filters.' : 'No package IDs found in the app list.'}</Text>}
              </Stack>
            </Stack>
          </ModalBody>
          <ModalFooter><Button onClick={() => setOpen(false)}>Done</Button></ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={Boolean(guideApp)} onClose={() => setGuidePackage(null)} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader pr={12}>Register {guideName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={5}>
              <Text as="code" fontSize="sm" overflowWrap="anywhere">{guidePackage}</Text>
              <Text fontSize="sm">Open Play Console → Manage account → Android developer verification → Package names → Register package name. Copy these three fields for this app:</Text>
              <Stack as="ol" aria-label="Registration fields" pl={5} spacing={4} listStyleType="decimal">
                {[
                  { label: 'Package name', value: guidePackage },
                  { label: 'App name', value: guideName },
                  { label: 'SHA-256', value: certificateFingerprint },
                ].map(({ label, value }) => <Box as="li" key={label} fontSize="sm">
                  <Text fontWeight="semibold">{label}{label === 'App name' ? ' (friendly name)' : ''}</Text>
                  <Text as="code" display="block" fontSize="xs" overflowWrap="anywhere" my={2}>{value || 'Loading signing certificate…'}</Text>
                  <Button size="sm" variant="outline" isDisabled={!value} onClick={() => copyValue(value, label)} aria-label={`Copy ${label} for ${guidePackage}`}>Copy {label}</Button>
                </Box>)}
              </Stack>
              <Box bg="gray.50" borderWidth="1px" borderRadius="lg" p={4}>
                <Text fontWeight="semibold" mb={3}>If Google asks you to sign and upload an APK</Text>
                <Stack as="ol" aria-label="APK ownership verification" start={4} pl={5} spacing={3} listStyleType="decimal" fontSize="sm">
                  <Box as="li">Copy the exact verification snippet shown by Google. Create <Text as="code" overflowWrap="anywhere">app/src/main/assets/adi-registration.properties</Text> in the Android app module and paste the snippet into it. Use the module’s actual directory if it is not named <code>app</code>.</Box>
                  <Box as="li">Build a release APK with package name <Text as="code" overflowWrap="anywhere">{guidePackage}</Text>, signed with the private key matching the SHA-256 above. For Bitrise, create the file in a Script step before the Android build, or include it in the verification build’s source.</Box>
                  <Box as="li">Upload the signed APK on Google’s “Sign and upload an APK” screen. You can use a minimal project with the same package name and signing key; you do not need to publish this verification APK to users.</Box>
                  <Box as="li">After Google confirms registration, return to the package list and press <strong>Check with Google</strong>. The registered indicator updates from the API result. You do not need to repeat the verification upload for every release.</Box>
                </Stack>
              </Box>
              <Text fontSize="xs" color="gray.600">The SHA-256 above applies to apps signed with this certificate. Complete any additional signing-key verification Google requests.</Text>
              <Flex gap={4} wrap="wrap">
                <Link href={CONSOLE_URL} isExternal color="orange.700" fontWeight="semibold">Open Play Console ↗</Link>
                <Link href={HELP_URL} isExternal color="orange.700">Google’s registration guide ↗</Link>
              </Flex>
            </Stack>
          </ModalBody>
          <ModalFooter><Button onClick={() => setGuidePackage(null)}>Back to packages</Button></ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
